// src/App.js
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import FullPageLoader from './components/FullPageLoader';
import { ThemeProvider } from '@mui/material/styles';
import { appTheme } from './theme/theme';

// Lazy-loaded pages
const LoginPage = React.lazy(() => import('./pages/Login/LoginPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/Login/ResetPassword'));

// Audit Pages
const AuditLanding = React.lazy(() => import('./pages/Audit/Landing'));
const AuditFinal = React.lazy(() => import('./pages/Audit/Final'));
const AuditItemsPage = React.lazy(() => import('./pages/Audit/AuditItems'));
const AuditLogs = React.lazy(() => import('./pages/Audit/Logs'));

// Images Pages
const ImagesLanding = React.lazy(() => import('./pages/Images/Landing'));
const ImagesProcessed = React.lazy(() => import('./pages/Images/Processed'));
const ImagesFinal = React.lazy(() => import('./pages/Images/Final'));
const ImagesLogs = React.lazy(() => import('./pages/Images/Logs'));
const AITrainingPage = React.lazy(() => import('./pages/Images/AITrainingPage'));
// const UndefinedPage = React.lazy(() => import('./pages/Images/UndefinedPage'));

// Barcodes Pages
const BarcodesLanding = React.lazy(() => import('./pages/Barcodes/Landing'));
const BarcodesFinal = React.lazy(() => import('./pages/Barcodes/Final'));
const BarcodesUnknown = React.lazy(() => import('./pages/Barcodes/Unknown'));
const BarcodesLibrary = React.lazy(() => import('./pages/Barcodes/Library'));
const BarcodesCompanies = React.lazy(() => import('./pages/Barcodes/Companies'));

// Admin Pages
const LoginHistory = React.lazy(() => import('./pages/Admin/LoginHistory'));
const UserManagement = React.lazy(() => import('./pages/Admin/UserManagement'));
const CreateUserForm = React.lazy(() => import('./pages/Admin/CreateNewUserForm'));
const EditUserForm = React.lazy(() => import('./pages/Admin/EditUserForm'));

// Countries Page
const Countries = React.lazy(() => import('./pages/Countries/Countries'));

// Manual_Search Page
const Manual_Search = React.lazy(() => import('./pages/Manual_Search/Manual_Search'));

// Routes Wrapper
const AppRoutes = () => {
  const { user, loading } = useAuth();
  
  if (loading) return <FullPageLoader />;
  
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {user && <Navbar />}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, sm: 3 },
          ml: user ? { xs: 0, sm: '240px' } : 0,
          transition: 'margin-left 0.3s ease',
          width: user ? 'calc(100% - 240px)' : '100%'
        }}
      >
        <Suspense fallback={<FullPageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={user ? <Navigate to="/audit/landing" replace /> : <LoginPage />} 
            />
            <Route 
              path="/reset-password" 
              element={user ? <Navigate to="/audit/landing" replace /> : <ResetPasswordPage />} 
            />

            {/* Audit Routes */}
            <Route 
              path="/audit/landing" 
              element={
                <ProtectedRoute>
                  <AuditLanding />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/audit/final" 
              element={
                <ProtectedRoute>
                  <AuditFinal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/audit/items" 
              element={
                <ProtectedRoute>
                  <AuditItemsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/audit/logs" 
              element={
                <ProtectedRoute>
                  <AuditLogs />
                </ProtectedRoute>
              } 
            />

            {/* Images Routes */}
            <Route 
              path="/images/landing" 
              element={
                <ProtectedRoute>
                  <ImagesLanding />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/images/processed" 
              element={
                <ProtectedRoute>
                  <ImagesProcessed />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/images/final" 
              element={
                <ProtectedRoute>
                  <ImagesFinal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/images/logs" 
              element={
                <ProtectedRoute>
                  <ImagesLogs />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/images/ai-training" 
              element={
                <ProtectedRoute>
                  <AITrainingPage />
                </ProtectedRoute>
              } 
            />
            {/*}
            <Route 
              path="/images/undefined" 
              element={
                <ProtectedRoute>
                  <UndefinedPage />
                </ProtectedRoute>
              } 
            />
            */}

            {/* Barcodes Routes */}
            <Route 
              path="/barcodes/landing" 
              element={
                <ProtectedRoute>
                  <BarcodesLanding />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/barcodes/final" 
              element={
                <ProtectedRoute>
                  <BarcodesFinal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/barcodes/unknown" 
              element={
                <ProtectedRoute>
                  <BarcodesUnknown />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/barcodes/library" 
              element={
                <ProtectedRoute>
                  <BarcodesLibrary />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/barcodes/companies" 
              element={
                <ProtectedRoute>
                  <BarcodesCompanies />
                </ProtectedRoute>
              } 
            />

            {/* Countries Route */}
            <Route 
              path="/countries" 
              element={
                <ProtectedRoute>
                  <Countries />
                </ProtectedRoute>
              } 
            />

            {/* Manual_Search Route */}
            <Route 
              path="/manual_search" 
              element={
                <ProtectedRoute>
                  <Manual_Search />
                </ProtectedRoute>
              } 
            />

            {/* Master Lists */}
            <Route 
              path="/masters/final_Audit&Images" 
              element={
                <ProtectedRoute>
                  <ImagesFinal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/masters/final_barcode" 
              element={
                <ProtectedRoute>
                  <BarcodesFinal />
                </ProtectedRoute>
              } 
            />

            {/* Admin Routes */}
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <UserManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users/new" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <CreateUserForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users/edit/:id" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <EditUserForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/login-history" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <LoginHistory />
                </ProtectedRoute>
              } 
            />

            {/* Catch-All Route */}
            <Route 
              path="*" 
              element={
                <Navigate to={user ? "/audit/landing" : "/login"} replace />
              } 
            />
            </Routes>
        </Suspense>
      </Box>
    </Box>
  );
};

// Main App
function App() {
  return (
    <ThemeProvider theme={appTheme}>
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;